import { t, translate, getLanguage } from 'react-switch-lang';
import { Form } from 'react-final-form';
import { useDispatch } from 'react-redux';
import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import mailcheck from 'mailcheck';

import ErrorAlert from '../ErrorAlert';
import * as Field from '../Field';
import Link from '../Link';
import MapleButton from '../MapleButton';
import { validateEmail, validatePassword } from '../../utils/Validation';
import { useCognito } from '../../utils/Cognito';
import { events, logAmpEvent } from '../../utils/Amplitude';
import { setEmail } from '../../redux/actions/AuthActions';
import { AUTH_STEP, AUTH_API } from '../../utils/Constants';
import { genCognitoErrHandler, getReCaptchaToken, invalidFormAlert } from '../../utils/Helpers';
import { setScreenReaderAlert } from '../../utils/Accessibility';
import GoogleRecaptchaDisclaimer from './GoogleRecaptchaDisclaimer';

const RegisterForm = ({ setStep }) => {
  const dispatch = useDispatch();
  const d = new Date();
  const utcDate = d.toISOString().substring(0, 10);
  const [errorMsg, setError] = useState('');
  const [showEmailConfirm, setShowEmailConfirm] = useState(false);
  const [invalidEmailSuggestion, setInvalidEmailSuggestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const cognito = useCognito();
  const reCaptchaRef = useRef(null);
  function getIp() {
    return fetch('https://api.ipify.org?format=json')
      .then((response) => response.json())
      .then((res) => res.ip)
      .catch(() => '000.000.000.000');
  }

  const mailcheckValidate = (value) => mailcheck.run({
    email: value,
    suggested(suggestion) {
      return suggestion.full;
    },
    empty() {
      return null;
    },
  });

  async function onSubmit(values) {
    setLoading(true);
    const email = values.email.toLowerCase().trim();

    if (invalidEmailSuggestion) return;

    const reCaptchaToken = getReCaptchaToken(reCaptchaRef.current, () => setLoading(false));
    if (!reCaptchaToken) return;

    const metadata = {
      reg_with: 'Web',
      reg_date: utcDate,
      reg_ip: await getIp(),
    };

    cognito
      .signUp(email, values.password, getLanguage(), reCaptchaToken, metadata)
      .then(() => {
        dispatch(setEmail(email));
        setScreenReaderAlert(t('ScreenReaderAlert_RegisterSuccess'));
        logAmpEvent(events.API_SUCCESS, { Call: `Cognito: ${AUTH_API.REGISTER}` });
        setStep(AUTH_STEP.CONFIRM_EMAIL);
      })
      .catch(genCognitoErrHandler(AUTH_API.REGISTER, setError))
      .finally(() => setLoading(false));
  }
  function recaptchaFailedCallback() {
    logAmpEvent(events.GOOGLE_RECAPTCHA_ERROR);
    setError(t('GoogleRecaptchaFailureMsg'));
    setLoading(false);
  }
  const validate = (values) => {
    const errors = {};
    errors.email = validateEmail(values.email);
    // Run mailcheck if email is valid
    if (!errors.email && !showEmailConfirm) {
      const email = values.email.toLowerCase().trim();
      const suggestedEmail = mailcheckValidate(email);
      setInvalidEmailSuggestion(suggestedEmail);
      if (suggestedEmail) {
        errors.email = t('Registration_MailCheck_EmailNotRight');
      }
    } else {
      setInvalidEmailSuggestion(null);
    }
    // MailCheck email confirm valiation
    if (showEmailConfirm && values.email !== values.confirmEmail) {
      errors.confirmEmail = t('Validation_Invalid_RegisterEmailEmailsNoMatch');
    }
    errors.password = validatePassword(values.password);
    return errors;
  };

  return (
    <Form
      onSubmit={onSubmit}
      validate={(values) => validate(values)}
      render={({ handleSubmit, invalid, errors, form: { submit, change } }) => (
        <div className="authForm">
          <form
            onSubmit={(event) => {
              invalidFormAlert(invalid, errors);
              handleSubmit(event);
            }}
            className="formContainer"
          >
            <div className="fieldsContainer">
              <ErrorAlert msg={errorMsg} />
              <ReCAPTCHA
                style={{ display: 'none' }}
                theme="dark"
                ref={reCaptchaRef}
                size="invisible"
                sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_KEY}
                onErrored={() => { recaptchaFailedCallback(); }}
                onChange={submit}
              />
              <h1 className="formTitle">{t('Create_Account_Title')}</h1>

              <Field.MailCheckEmail
                label={t('Auth_Field_Email_Lbl')}
                id="EmailInput"
                name="email"
                invalidEmailSuggestion={invalidEmailSuggestion}
                setInvalidEmailSuggestion={setInvalidEmailSuggestion}
                change={change}
                setShowEmailConfirm={setShowEmailConfirm}
                showEmailConfirm={showEmailConfirm}
              />

              {showEmailConfirm && (
                <Field.Default
                  label={t('Registration_Field_ConfirmEmail_Lbl')}
                  id="ConfirmEmailInput"
                  name="confirmEmail"
                  autoComplete="email"
                  placeholder={t('Auth_Field_Email_Placeholder')}
                />
              )}

              <Field.Password
                label={t('Auth_Field_Password_Lbl')}
                id="PasswordInput"
                name="password"
                showRules
              />
              <MapleButton
                color="dark"
                text={t('Create_Account_Btn')}
                type="submit"
                className="mt-4 mb-2 px-5"
                ampEvent="USER_CLICKED_REGISTER_BUTTON"
                loading={loading}
                onClickEvent={() => {
                  setError('');
                }}
              />
              <GoogleRecaptchaDisclaimer />
              <p>
                {t('Registration_HaveAccount_Lbl')}
                {' '}
                <Link
                  href="/login"
                  ampEvent={events.USER_CLICKED_LOGIN_LINK_FROM_REGISTER}
                >
                  {t('Registration_Login_Link')}
                </Link>
              </p>
            </div>
          </form>
        </div>
      )}
    />
  );
};

export default translate(RegisterForm);
